import React, {useEffect, useState} from 'react'
import ProgramResourcesView from '../../views/ver_24.10/ProgramResources.view'
import PptxGenJS from "pptxgenjs";
import {fetchProgramPreviewService, fetchProgramSessionService} from "../../services/program.service";
import {useParams} from "react-router-dom";
import {
    PBDQ3_PROGRAM_SESSION_CONTENT_LAYOUT,
    PBDQ3_PROGRAM_SESSION_CONTENT_LAYOUT_VERBOSE, TOAST_TYPES
} from "../../utils/constants";
import Loader from "../../ui_library/ver_24.10/Loader/Loader.comp";
import {toast} from "../../utils/utils";

const ProgramResources = () => {
    const { programId } = useParams();
    const [programData, setProgramData] = useState({});
    const [modulesData, setModulesData] = useState([]);
    const [showLoader, setShowLoader] = useState(false);

    useEffect(() => {
        (async () => {
            if (programId) {
                setShowLoader(true);
                try {
                    const result = await fetchProgramPreviewService(programId);
                    if(result.data) {
                        setProgramData(result.data.programData);
                        setModulesData(result.data.modulesData);
                    }
                } catch (error) {
                    console.error('Failed to fetch program status:', error);
                }
                setShowLoader(false);
            }
        })();
    }, [programId]);


    const data = {
    };

    const options={
    };

    const actions = {
        generatePPTFromModules: async () => {
            console.log("Generating PowerPoint from modules...");
            toast(TOAST_TYPES.SUCCESS, "Success", "PowerPoint generation started!");
            setTimeout(() => setShowLoader(true), 500);

            let pptx = new PptxGenJS();

            // Basic conversions/params
            const pxToPt = (px) => px * 0.75;
            const PPT_WIDTH = 10;       // 16:9 slides => 10" wide
            const PPT_HEIGHT = 5.63;    // 16:9 slides => ~5.63" high

            // We'll define a centered content area for the two columns.
            const TOTAL_CONTENT_WIDTH = 8.3;
            const TOTAL_CONTENT_HEIGHT = 4.0;
            const X_CENTER = (PPT_WIDTH - TOTAL_CONTENT_WIDTH) / 2;   // ~0.85
            const Y_CENTER = (PPT_HEIGHT - TOTAL_CONTENT_HEIGHT) / 2; // ~0.815
            const COL_GAP = 0.3;
            const COL_WIDTH = (TOTAL_CONTENT_WIDTH - COL_GAP) / 2; // => 4.0
            const COL_HEIGHT = TOTAL_CONTENT_HEIGHT;              // => 4.0

            // Sizing for images so height=width (square)
            const SQUARE_IMAGE_SIZE = 3.5;

            // 1) COVER SLIDE
            const coverSlide = pptx.addSlide();
            coverSlide.background = { color: "#FF6F3F" };
            coverSlide.addText(`Program Name: ${programData.course_name}`, {
                x: 0.5,
                y: 1.5,
                w: 9,
                fontSize: pxToPt(50),
                bold: true,
                color: "#FFFFFF",
                align: "left",
            });

            /**
             * createTextSegments:
             * Convert an array of { PointTitle, PointText } items into partial-bold text segments.
             */
            function createTextSegments(contentArr, boldFontSize, normalFontSize) {
                let segments = [];
                contentArr.forEach((item, idx) => {
                    const hasTitle = item.PointTitle && item.PointTitle.trim().length > 0;
                    if (hasTitle) {
                        segments.push({
                            text: `${item.PointTitle}: `,
                            options: {
                                bold: true,
                                fontSize: pxToPt(boldFontSize),
                                color: "#000000",
                            },
                        });
                    }
                    segments.push({
                        text: item.PointText,
                        options: {
                            fontSize: pxToPt(normalFontSize),
                            color: "#000000",
                        },
                    });
                    // Line break after each item except the last
                    if (idx < contentArr.length - 1) {
                        segments.push({ text: "\n", options: {} });
                    }
                });
                return segments;
            }

            // 2) Iterate modules -> units -> lessons
            for (const module of modulesData) {
                for (const unit of module.units) {
                    for (const session of unit.lessons) {
                        try {
                            // a) Create a "session intro" slide (like cover slide)
                            const introSlide = pptx.addSlide();
                            introSlide.background = { color: "#FF6F3F" };
                            // We'll place module, unit, session each on its own line, large text
                            introSlide.addText(
                                [
                                    {
                                        text: `${module.module_name}\n`,
                                        options: {
                                            fontSize: pxToPt(34),
                                            bold: true,
                                            color: "#FFFFFF",
                                        },
                                    },
                                    {
                                        text: `${unit.unit_title}\n`,
                                        options: {
                                            fontSize: pxToPt(30),
                                            bold: true,
                                            color: "#FFFFFF",
                                        },
                                    },
                                    {
                                        text: `${session.lesson_title}`,
                                        options: {
                                            fontSize: pxToPt(26),
                                            bold: true,
                                            color: "#FFEB3B",
                                        },
                                    },
                                ],
                                {
                                    x: 0.5,
                                    y: 1.0,
                                    w: 9,
                                    h: 3,
                                    align: "left",
                                    valign: "top",
                                    wrap: true,
                                    shrinkText: true,
                                }
                            );

                            // b) Fetch content slides for this session
                            const result = await fetchProgramSessionService(session.lesson_code);
                            const slidesData = result.data.sessionsData;

                            for (const slideData of slidesData) {
                                const { SlideTitle, SlideContent, Images } = slideData;

                                // Normalize SlideContent
                                const formattedContent = Array.isArray(SlideContent)
                                    ? SlideContent.map((item) =>
                                        typeof item === "string"
                                            ? { PointTitle: "", PointText: item }
                                            : item
                                    )
                                    : [{ PointTitle: "", PointText: SlideContent }];

                                // Create the new slide
                                const slide = pptx.addSlide();
                                slide.background = { color: "#FFFFFF" };

                                const hasImage = Images && Images.length > 0;

                                // -- 2-column layout, centered horizontally + vertically
                                if (hasImage) {
                                    //
                                    // LEFT COLUMN => Title + text
                                    //
                                    // Title bounding box at top of left column
                                    slide.addText(SlideTitle, {
                                        x: X_CENTER,
                                        y: Y_CENTER, // top of content region
                                        w: COL_WIDTH,
                                        h: 0.6, // enough for single-line of 41px
                                        fontSize: pxToPt(41),
                                        bold: true,
                                        color: "#ED6628",
                                        align: "left",
                                        wrap: true,
                                        shrinkText: true,
                                    });

                                    // Text bounding box just below the title
                                    const textSegments = createTextSegments(formattedContent, 23, 21);
                                    slide.addText(textSegments, {
                                        x: X_CENTER,
                                        y: Y_CENTER + 0.9, // push it lower to avoid overlap
                                        w: COL_WIDTH,
                                        h: COL_HEIGHT - 0.8, // 4.0 - 0.8 => 3.2" for text
                                        align: "left",
                                        wrap: true,
                                        shrinkText: true,
                                        valign: "top",
                                    });

                                    //
                                    // RIGHT COLUMN => Image, centered vertically in that column
                                    //
                                    const imageTop =
                                        Y_CENTER + (COL_HEIGHT - SQUARE_IMAGE_SIZE) / 2; // vertical center
                                    slide.addImage({
                                        path: Images[0],
                                        x: X_CENTER + COL_WIDTH + COL_GAP,
                                        y: imageTop,
                                        w: SQUARE_IMAGE_SIZE,
                                        h: SQUARE_IMAGE_SIZE,
                                        sizing: { type: "contain", w: SQUARE_IMAGE_SIZE, h: SQUARE_IMAGE_SIZE },
                                    });
                                } else {
                                    //
                                    // NO IMAGE => left col is Title only, right col is text
                                    //
                                    slide.addText(SlideTitle, {
                                        x: X_CENTER,
                                        y: Y_CENTER,
                                        w: COL_WIDTH,
                                        h: COL_HEIGHT,
                                        fontSize: pxToPt(41),
                                        bold: true,
                                        color: "#ED6628",
                                        align: "left",
                                        wrap: true,
                                        shrinkText: true,
                                        valign: "top",
                                    });

                                    // Text in the right column
                                    const textSegments = createTextSegments(formattedContent, 23, 21);
                                    slide.addText(textSegments, {
                                        x: X_CENTER + COL_WIDTH + COL_GAP,
                                        y: Y_CENTER,
                                        w: COL_WIDTH,
                                        h: COL_HEIGHT,
                                        align: "left",
                                        wrap: true,
                                        shrinkText: true,
                                        valign: "top",
                                    });
                                }
                            }
                        } catch (error) {
                            console.error(`Failed to fetch session data for ${session.lesson_code}`, error);
                        }
                    }
                }
            }

            // Save the PPT
            await pptx.writeFile({ fileName: `${programData.course_name}.pptx` });
            setShowLoader(false);
            console.log("PowerPoint generated successfully!");
        },
    };
    if (showLoader) {
        return <Loader />;
    }
    return (
        <>
            <ProgramResourcesView data={data} options={options} actions={actions}/>
        </>
    );
}

export default ProgramResources;